import { useEffect, useMemo, useState } from "react";

import { Outlet } from "react-router-dom";

import {
  ArchiveBoxArrowDownIcon,
  ArchiveBoxIcon,
  ArrowUturnUpIcon,
  BanknotesIcon,
  BookOpenIcon,
  BriefcaseIcon,
  BuildingOfficeIcon,
  BuildingStorefrontIcon,
  CalculatorIcon,
  CalendarDaysIcon,
  CameraIcon,
  ClipboardDocumentCheckIcon,
  ClockIcon,
  Cog6ToothIcon,
  CurrencyPoundIcon,
  DocumentChartBarIcon,
  DocumentMagnifyingGlassIcon,
  DocumentTextIcon,
  FolderPlusIcon,
  HomeIcon,
  InboxArrowDownIcon,
  InboxStackIcon,
  MagnifyingGlassPlusIcon,
  NewspaperIcon,
  ReceiptRefundIcon,
  RectangleGroupIcon,
  RocketLaunchIcon,
  ShieldExclamationIcon,
  TagIcon,
  TruckIcon,
  WrenchScrewdriverIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";

import Topbar from "../components/shared/Navbar/Topbar";
import LoadingWheel from "../components/shared/LoadingWheel";
import { NavigationItemInterface } from "../types/Navigation";
import { usePageNameContext } from "../contexts/PageTitleContext";
import MobileSidebar from "../components/shared/Navbar/MobileSidebar";
import DesktopSidebar from "../components/shared/Navbar/DesktopSidebar";
import { useAuth } from "../contexts/AuthContext";
import useCustomerDetails from "../data/useCustomerDetails";
import { GetFullBrandResponse } from "../models/Customer";
import { wilmaSSOAppRegistration, wilmaSSODashboardUrl } from "../config";
import useNotificationStats from "../data/useNotificationStats";
import { INotificationStats } from "../models/notifications/Notifications";

export default function DashboardLayout() {
  const {
    pageInfo: { name, desc, fill },
  } = usePageNameContext();
  const { loading, loggedIn, claims } = useAuth();
  const { brand, features } = useCustomerDetails();
  const { notifications } = useNotificationStats(loading);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [featureFlags, setFeatureFlags] = useState<string[] | undefined>(undefined);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem("sidebarCollapsed") === "true";
    } catch {
      return false;
    }
  });

  const tailwindShades = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900];

  // Function to create/update favicon
  const updateIcon = (rel: string, size: string | null, href: string) => {
    let selector = size ? `link[rel='${rel}'][sizes='${size}']` : `link[rel='${rel}']`;
    let link = document.querySelector(selector) as HTMLLinkElement;
    if (!link) {
      link = document.createElement("link");
      link.rel = rel;
      if (size) link.setAttribute("sizes", size);
      document.head.appendChild(link);
    }
    link.href = `${href}?v=${new Date().getTime()}`;
  };

  const handleUpdateIcons = (brand: GetFullBrandResponse) => {
    const { faviconUrl, favicon16Url, favicon32Url, appleTouchIconUrl, androidChrome192Url, androidChrome512Url } = brand;

    //favicon
    updateIcon("icon", "16x16", favicon16Url);
    updateIcon("icon", "32x32", favicon32Url);
    updateIcon("icon", null, faviconUrl);
    updateIcon("shortcut icon", null, faviconUrl);

    if (appleTouchIconUrl)
      updateIcon("apple-touch-icon", null, appleTouchIconUrl);

    if (androidChrome192Url)
      updateIcon("icon", "192x192", androidChrome192Url);

    if (androidChrome512Url)
      updateIcon("icon", "512x512", androidChrome512Url);

  }

  useEffect(() => {
    if (brand.isLoading || features.isLoading) return;

    if (brand.data?.data && tailwindShades.length > 0 && brand.data.data.primaryTailwindColours) {
      console.log("Brand data loaded:", brand.data.data);
      // Set the brand colours
      tailwindShades.forEach((shade, index) => {
        if (!brand.data.data.primaryTailwindColours[shade]) {
          console.warn(`Primary tailwind colour for shade ${shade} is not defined.`);
        } else document.documentElement.style.setProperty(`--primary-${shade}`, brand.data.data.primaryTailwindColours[shade]);
      });

      handleUpdateIcons(brand.data.data);
    }

    if (!features.data) {
      console.log("no feature data")
      return;
    }

    const flags = features.data?.data.map((feature) => feature.name);
    setFeatureFlags(flags);

  }, [brand.isLoading, features.isLoading, brand.data, features.data, notifications.data]);

  useEffect(() => {
    if (claims && !loading) {
      if (claims?.isExporter) {
        //if at / then redirect exporter to exports
        if (window.location.pathname === "/") {
          window.location.href = "/exports";
        }
      }
    }
  }, [claims, loading, window.location.pathname]);

  useEffect(() => {
    try {
      localStorage.setItem("sidebarCollapsed", isSidebarCollapsed.toString());
    } catch { }
  }, [isSidebarCollapsed]);

  if (!loading && !loggedIn) {
    window.location.assign(
      `${wilmaSSODashboardUrl}?ari=${wilmaSSOAppRegistration}&returnUrl=${window.location.href}`
    );
  }

  const staffNavigation = (featureFlags: string[] | undefined, notifications: INotificationStats | undefined): NavigationItemInterface[] => {
    if (!featureFlags) return [];

    return [
      { name: "Dashboard", to: "/", icon: HomeIcon },

      //** Leads **/
      { name: "Leads", to: "leads", icon: FolderPlusIcon, group: "Leads", disabled: !featureFlags.includes("Lead Management"), notificationCount: notifications?.leadsAwaitingContact },
      { name: "Collected", to: "vehicles-collected", icon: ArchiveBoxIcon, group: "Leads", disabled: !featureFlags.includes("Lead Management") },
      //{ name: "Calendar", to: "calendar", icon: CalendarDaysIcon },
      { name: "Trip Manager", to: "trip-manager", icon: CalendarDaysIcon, group: "Leads", disabled: !featureFlags.includes("Lead Management") },
      { name: "Drop Offs", to: "drop-offs", icon: InboxArrowDownIcon, group: "Leads", disabled: !featureFlags.includes("Lead Management") },
      { name: "Schedule", to: "schedule", icon: NewspaperIcon, group: "Leads", disabled: !featureFlags.includes("Lead Management") },

      // ** Accounts **/
      { name: "Accounts", to: "accounts", icon: BuildingOfficeIcon, group: "Accounts", disabled: false },
      { name: "Invoices", to: "invoices", icon: DocumentTextIcon, group: "Accounts", disabled: false },

      //** Payments **/
      { name: "Payments", to: "payments", icon: BanknotesIcon, group: "Leads", disabled: !featureFlags.includes("Customer Payments"), }, //Not working - notificationCount: notifications?.leadsRaisedPayments 

      //** Parts **/
      { name: "Awaiting Inventory", to: "parts-manager", icon: WrenchScrewdriverIcon, group: "Parts", disabled: !featureFlags.includes("Parts Tracker"), notificationCount: notifications?.vehiclesAwaitingSchedule },
      { name: "Exports", to: "exports", icon: RocketLaunchIcon, group: "Parts", disabled: !featureFlags.includes("Parts Tracker") },
      { name: "Vehicles", to: "vehicles", icon: TruckIcon, group: "Parts", disabled: !featureFlags.includes("Parts Tracker") },
      { name: "Awaiting Arrival", to: "awaiting-arrival", icon: ClockIcon, group: "Parts", disabled: !featureFlags.includes("Lead Management") },
      { name: "Production Manager", to: "production-manager", icon: RectangleGroupIcon, group: "Parts", disabled: false },
      { name: "Parts Checker", to: "parts-checker", icon: ClipboardDocumentCheckIcon, group: "Parts", disabled: !featureFlags.includes("Parts Tracker") },
      { name: "Jobs", to: "jobs", icon: BriefcaseIcon, group: "Parts", disabled: !featureFlags.includes("Parts Tracker") },
      { name: "Stock", to: "stock", icon: InboxStackIcon, group: "Parts", disabled: !featureFlags.includes("Parts Tracker") },
      { name: "Quality Control", to: "stock-qc", icon: ShieldExclamationIcon, group: "Parts", disabled: !featureFlags.includes("Parts Tracker") },

      //** eBay **/
      { name: "eBay Unlisted", to: "ebay-unlisted", icon: DocumentTextIcon, group: "Order Management", disabled: !featureFlags.includes("eBay Management"), notificationCount: notifications?.partsUnlisted },
      { name: "Recently Listed", to: "recently-listed", icon: ReceiptRefundIcon, group: "Order Management", disabled: !featureFlags.includes("eBay Management") },
      { name: "eBay Listings", to: "ebay-listings", icon: CurrencyPoundIcon, group: "Order Management", disabled: !featureFlags.includes("eBay Management") },
      { name: "Orders", to: "ebay-orders", icon: ArchiveBoxArrowDownIcon, group: "Order Management", disabled: !featureFlags.includes("eBay Management"), notificationCount: notifications?.partsNewOrders },
      { name: "eBay Returns", to: "ebay-returns", icon: ArrowUturnUpIcon, group: "Order Management", disabled: !featureFlags.includes("eBay Management") },

      //** Widget **/
      { name: "Dashboard", to: "widget-dashboard", icon: HomeIcon, group: "Widget" },
      { name: "Calculator", to: "widget-calculator", icon: CalculatorIcon, group: "Widget" },
      { name: "Postcodes", to: "widget-postcodes", icon: DocumentMagnifyingGlassIcon, group: "Widget" },
      { name: "Suppliers", to: "widget-suppliers", icon: BuildingStorefrontIcon, group: "Widget" },
      { name: "SellAVehicle", to: "sellavehicle", icon: TruckIcon, group: "Widget" },
      { name: "History", to: "abandoned-quotes", icon: BookOpenIcon, group: "Widget" },
      { name: "Configuration", to: "widget-config", icon: Cog6ToothIcon, group: "Widget" },
      // { name: "Contacts", to: "contacts", icon: UsersIcon },
    ];
  };

  const driverNavigation: NavigationItemInterface[] = [
    { name: "Find Lead", to: "find-lead", icon: DocumentMagnifyingGlassIcon },
  ];

  const exporterNavigation: NavigationItemInterface[] = [
    { name: "Exports", to: "exports", icon: RocketLaunchIcon },
  ];

  const adminNavigation = (featureFlags: string[] | undefined): NavigationItemInterface[] => {
    if (!featureFlags) return [];

    return [
      //** Admin **/
      { name: "Reports", to: "reports", icon: DocumentChartBarIcon, group: "Admin" },
      { name: "Find Lead", to: "find-lead", icon: DocumentMagnifyingGlassIcon, group: "Admin" },
      { name: "Part Categorisation", to: "parts-editor", icon: RectangleGroupIcon, group: "Admin" },
      { name: "Category Editor", to: "category-editor", icon: TagIcon, group: "Admin" },
      { name: "Required Images", to: "category-images", icon: CameraIcon, group: "Admin" },
      { name: "Driver Tracking", to: "driver-tracking", icon: MagnifyingGlassPlusIcon, group: "Admin", disabled: !featureFlags.includes("Driver Tracking") },
      { name: "Admin Settings", to: "admin", icon: Cog6ToothIcon, group: "Admin" },
    ];
  };

  //Get the url and check if we're at a disabled page and redirect to home
  const allNavigation: NavigationItemInterface[] = [
    ...staffNavigation(featureFlags, notifications.data?.data),
    ...driverNavigation,
    ...adminNavigation(featureFlags),
  ];

  const url = window.location.pathname.replace("/", "");
  const splits = url.split("/");

  const page = allNavigation.find((nav) => splits.includes(nav.to));
  if (page && page.disabled) {
    window.location.href = "/";
  }


  const getNavigation = (claims: any, featureFlags: string[] | undefined): NavigationItemInterface[] => {
    if (!claims || !featureFlags) return [];

    let navigation = [];

    if (claims.isAdmin) {
      navigation.push(
        ...staffNavigation(featureFlags, notifications.data?.data),
        ...adminNavigation(featureFlags)
      );
    } else if (claims.isDriver) {
      navigation.push(...driverNavigation);
    } else if (claims.isExporter) {
      navigation.push(...exporterNavigation);
    } else {
      navigation.push(...staffNavigation(featureFlags, notifications.data?.data));
    }

    return navigation;
  };

  const navigation = useMemo(() => getNavigation(claims, featureFlags), [claims, featureFlags]);

  if (loading || !featureFlags) {
    return (
      <div className="h-screen w-screen flex items-center justify-center">
        <LoadingWheel />
      </div>
    );
  }

  return (
    <div>
      <Topbar collapsed={isSidebarCollapsed} onToggleCollapsed={() => setIsSidebarCollapsed((prev) => !prev)} setSidebarOpen={setSidebarOpen} />

      <div>
        <MobileSidebar
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          navigation={navigation}
        />
        <DesktopSidebar
          navigation={navigation}
          collapsed={isSidebarCollapsed}
            
        />
        <main className={`flex flex-1 flex-col transition-all ${isSidebarCollapsed ? "md:pl-16" : "md:pl-60"}`} style={{ position: "relative", zIndex: 0, overflow: "visible" }}>
          <div className={`${!fill && "pb-6 pt-2"}`}>
            {!fill && (
              <div className="mt-2 px-4 sm:px-6 md:pl-4 prose">
                <h1 className="font-semibold text-gray-900 mb-0">{name}</h1>
                <h4 className="text-base font-medium text-gray-500 mt-1">
                  {desc}
                </h4>
              </div>
            )}
            <div className={`${!fill && "px-4 sm:px-6 md:px-4"}`}>
              <Outlet />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
